import { apiClient } from './config.js';
import type {
  PaginatedResponse,
  ArticleSummary,
  ArticleDetail,
  SuccessResponse,
} from './types.js';

/**
 * List articles with pagination
 * System admins see all articles, regular users see only their tenant's articles
 */
export async function listArticles(params?: {
  page?: number;
  limit?: number;
  /** Filter by publish_status (e.g. 'review_blocked' for the review queue). */
  status?: string;
}): Promise<PaginatedResponse<ArticleSummary>> {
  const response = await apiClient.get<PaginatedResponse<ArticleSummary>>(
    '/articles',
    { params }
  );
  return response.data;
}

/** The Articles-page lanes (backend services/article_lanes.py). */
export type ArticleLane = 'per_entity' | 'sitewide' | 'syntheses' | 'direct_input';
export const ARTICLE_LANES: ArticleLane[] = ['per_entity', 'sitewide', 'syntheses', 'direct_input'];
export type LaneDayCounts = { total: number } & Record<ArticleLane, number>;

/**
 * Per-date counts split by lane, using the rule the gallery lists by, so the
 * lane counts sum to `total` (the day badge). Returns { 'YYYY-MM-DD': {...} }.
 */
export async function getArticleLaneDateCounts(params: {
  from: string;
  to: string;
  status?: string;
  entity_group?: string;
  include_archived?: boolean;
}): Promise<Record<string, LaneDayCounts>> {
  const { data } = await apiClient.get<Record<string, LaneDayCounts>>('/articles/date-counts', {
    params: { ...params, by_lane: true },
  });
  return data;
}

/**
 * Get article details with full content
 */
export async function getArticle(articleId: string): Promise<ArticleDetail> {
  const response = await apiClient.get<ArticleDetail>(`/articles/${articleId}`);
  return response.data;
}

/**
 * Mark an article as viewed by the current user
 * Used for "new" badge tracking
 */
export async function markArticleViewed(articleId: string): Promise<SuccessResponse> {
  const response = await apiClient.post<SuccessResponse>(`/articles/${articleId}/view`);
  return response.data;
}

/**
 * Get signed URL for the source announcement PDF
 * Returns a temporary URL (valid for 60 minutes) to view/download the original PDF
 */
export async function getSourcePdfUrl(articleId: string): Promise<{ pdf_url: string; expires_in_minutes: number }> {
  const response = await apiClient.get<{ pdf_url: string; expires_in_minutes: number }>(`/articles/${articleId}/source-pdf-url`);
  return response.data;
}

/**
 * Delete an article
 * This is a permanent action that cannot be undone.
 * Will return 409 if the article is currently published.
 */
export async function deleteArticle(articleId: string): Promise<SuccessResponse> {
  const response = await apiClient.delete<SuccessResponse>(`/articles/${articleId}`);
  return response.data;
}

/**
 * Image-history metadata. Drives can_undo / can_redo button visibility on
 * the article-detail action menu (mig 185).
 */
export interface ImageHistoryStatus {
  article_id: string;
  can_undo: boolean;
  can_redo: boolean;
  head: {
    history_id: string;
    change_kind: 'initial' | 'reselect' | 'manual_edit';
    created_at: string;
    created_by: string | null;
    engine_version: string | null;
    diff_summary: Record<string, any> | null;
  } | null;
  total_count: number;
}

export async function getArticleImageHistory(
  articleId: string,
): Promise<ImageHistoryStatus> {
  const r = await apiClient.get<ImageHistoryStatus>(`/articles/${articleId}/image-history`);
  return r.data;
}

/**
 * Undo image-selection one step. Moves the article's image_history_head
 * pointer to the prior snapshot and copies that snapshot's prepared_images
 * back. Returns 409 when no prior snapshot exists.
 */
export async function undoArticleImages(
  articleId: string,
  expectedUpdatedAt: string | null,
  queueRepublish: boolean = true,
): Promise<{
  applied: boolean;
  article_id: string;
  updated_at: string;
  direction: 'undo';
  new_head: { history_id: string; change_kind: string; created_at: string };
  publish_jobs: Array<{ job_id: string; target: string; destination: string }>;
}> {
  const r = await apiClient.post(`/articles/${articleId}/undo-images`, {
    expected_updated_at: expectedUpdatedAt,
    queue_republish: queueRepublish,
  });
  return r.data as any;
}

export async function redoArticleImages(
  articleId: string,
  expectedUpdatedAt: string | null,
  queueRepublish: boolean = true,
): Promise<{
  applied: boolean;
  article_id: string;
  updated_at: string;
  direction: 'redo';
  new_head: { history_id: string; change_kind: string; created_at: string };
  publish_jobs: Array<{ job_id: string; target: string; destination: string }>;
}> {
  const r = await apiClient.post(`/articles/${articleId}/redo-images`, {
    expected_updated_at: expectedUpdatedAt,
    queue_republish: queueRepublish,
  });
  return r.data as any;
}

/**
 * Re-run image selection for an article using the current scoring engine.
 *
 * Surgical alternative to a full regeneration: no AI body-write, no PDF
 * re-extraction. Backend re-runs ONLY the cynapse image fetch + the
 * topic-relevance / recency / ownership / featured-image gate stack,
 * then writes new prepared_images, NULLs prepared_content (publish flow
 * re-renders), and optionally enqueues republish-to-test for each
 * active target so the operator can preview before promote.
 *
 * Returns a diff so the UI can show what changed (or "no change").
 */
/**
 * One entry in an article's curated `prepared_images`.
 *
 * Lives here rather than in the panel because it is the shape of API data:
 * the re-select dry-run, the revision reader and the images panel all speak
 * it, and a local copy in one component made the other two untypeable.
 */
export interface PreparedImage {
  /** Cynapse image id. NULL for the generated headline card (`role:
   *  'headline_card'`), which is rendered per article rather than
   *  extracted — never key a list on it or fetch image detail for it. */
  image_id?: string | null;
  role?: 'featured' | 'inline' | 'headline_card' | string;
  image_role?: string;
  image_type?: string;
  thumbnail_url?: string;
  inline_url?: string;
  url?: string;
  hero_url?: string;
  social_url?: string;
  alt?: string;
  caption?: string | null;
  quality_score?: number;
  topic_relevance?: number | null;
  most_recent_use?: string | null;
  provenance?: {
    source?: string;
    // Headline-card fields (MASTHEAD_HEADLINE_CARDS brief, 2026-09-15).
    template_version?: number;
    masthead?: string;
    digest?: string;
    headline?: string;
    /** True when the headline was cut at a clause boundary to fit (D5). */
    shortened?: boolean;
    ticker?: string | null;
    exchange?: string | null;
    date?: string;
    synthesis?: boolean;
  } | null;
  is_hero_eligible?: boolean | null;
  is_body_eligible?: boolean | null;
  is_social_eligible?: boolean | null;
}

export interface ReselectResult {
  reselected: boolean;
  dry_run?: boolean;
  article_id: string;
  updated_at?: string;
  changed: boolean;
  before: { featured_image_id: string | null; inline_count: number };
  after: { featured_image_id: string | null; inline_count: number };
  /** dry_run only — what the engine WOULD pick. Stage this, don't persist it. */
  proposed_images?: PreparedImage[];
  /** commit only — what was actually persisted. */
  resulting_images?: PreparedImage[];
  /** commit only — set when the commit differed from the preview. */
  diverged_from_preview?: boolean;
  divergence?: {
    previewed_image_ids: string[];
    committed_image_ids: string[];
    featured_changed: boolean;
    added: string[];
    removed: string[];
  } | null;
  publish_jobs: Array<{ job_id: string; target: string; destination: string }>;
}

export async function reselectArticleImages(
  articleId: string,
  expectedUpdatedAt: string | null,
  queueRepublish: boolean = true,
  opts?: { dryRun?: boolean; previewedImageIds?: string[]; previewOnly?: boolean },
): Promise<ReselectResult> {
  const response = await apiClient.post(`/articles/${articleId}/reselect-images`, {
    expected_updated_at: expectedUpdatedAt,
    queue_republish: queueRepublish,
    dry_run: opts?.dryRun ?? false,
    // Echoed back on COMMIT so the server can tell us whether the engine
    // picked differently than we showed. The engine is a deterministic argmax
    // but its inputs move — pool additions, recency decay — so a commit
    // minutes after a preview can legitimately differ. Advisory only.
    previewed_image_ids: opts?.previewedImageIds ?? null,
    preview_only: opts?.previewOnly ?? false,
  });
  return response.data as ReselectResult;
}

/**
 * Result of the two headline-card routes.
 *
 * `headline_card` is the `prepared_images` entry after the call (null once
 * suppressed, or when the render failed and the backend fell through to the
 * static card); `suppressed` echoes `article_metadata.headline_card_suppressed`.
 */
export interface HeadlineCardResult {
  updated: boolean;
  article_id: string;
  updated_at: string;
  headline_card: PreparedImage | null;
  suppressed: boolean;
  publish_jobs: Array<{ job_id: string; target: string; destination: string }>;
}

/**
 * Re-render the per-article headline card (masthead + headline + ticker +
 * date) and replace its `prepared_images` entry. The card only ships when no
 * announcement image applies, so this is a no-op for readers of an article
 * that has a hero. Optimistic-lock token exactly as `reselectArticleImages`.
 */
export async function regenerateHeadlineCard(
  articleId: string,
  expectedUpdatedAt: string | null,
  queueRepublish: boolean = true,
): Promise<HeadlineCardResult> {
  const response = await apiClient.post(`/articles/${articleId}/headline-card/regenerate`, {
    expected_updated_at: expectedUpdatedAt,
    queue_republish: queueRepublish,
  });
  return response.data as HeadlineCardResult;
}

/**
 * Suppress (or un-suppress) the generated headline card for ONE article.
 * Suppressed articles fall back to the masthead's static default card; the
 * flag is independent of the raw-image fallback suppression in
 * `suppressArticleImageFallback`.
 */
export async function suppressHeadlineCard(
  articleId: string,
  suppressed: boolean,
  expectedUpdatedAt: string | null,
  queueRepublish: boolean = true,
): Promise<HeadlineCardResult> {
  const response = await apiClient.post(`/articles/${articleId}/headline-card/suppress`, {
    suppressed,
    expected_updated_at: expectedUpdatedAt,
    queue_republish: queueRepublish,
  });
  return response.data as HeadlineCardResult;
}

/** One entry in an article's image-revision timeline. */
export interface ImageRevision {
  history_id: string;
  change_kind: 'initial' | 'reselect' | 'manual_edit';
  created_at: string | null;
  created_by: string | null;
  engine_version: string | null;
  diff_summary: Record<string, any> | null;
  image_count: number;
  is_head: boolean;
}

/**
 * The full image-revision timeline.
 *
 * Every revision's complete image set has been stored since mig 185, but until
 * now only +/-1 from head was reachable (undo/redo), and diff_summary — which
 * records what each edit actually did — was fetched and never shown.
 */
export async function listImageRevisions(
  articleId: string,
): Promise<{ article_id: string; head_history_id: string | null; revisions: ImageRevision[] }> {
  const r = await apiClient.get(`/articles/${articleId}/image-history/revisions`);
  return r.data as any;
}

/**
 * Read one revision's images WITHOUT moving the head pointer.
 *
 * This is what makes undo/redo stageable: previously the only way to see a
 * previous selection was to APPLY it.
 */
export async function readImageRevision(
  articleId: string,
  historyId: string,
): Promise<{ history_id: string; change_kind: string; images: PreparedImage[]; diff_summary: Record<string, any> | null }> {
  const r = await apiClient.get(`/articles/${articleId}/image-history/${historyId}/images`);
  return r.data as any;
}

// ---------------------------------------------------------------------------
// Phase B — surgical image edits (remove, replace, browse picker)
// ---------------------------------------------------------------------------

export interface EntityImageRow {
  image_id: string;
  cdn_url: string;
  thumbnail_url?: string;
  inline_url?: string;
  hero_url?: string;
  image_role?: string;
  image_type?: string;
  quality_score?: number;
  ownership_type?: string;
  is_hero_eligible?: boolean | null;
  is_body_eligible?: boolean | null;
  topic_relevance?: number | null;
  recency_score?: number | null;
  combined_score?: number;
  ai_description?: {
    description?: string;
    suggested_caption?: string;
    suggested_alt_text?: string;
    key_subjects?: string[];
  } | null;
}

export interface EntityImagesResponse {
  article_id: string;
  entity_id: string | null;
  entity_display_name: string | null;
  cynapse_entity_id?: string | null;
  image_count: number;
  images: EntityImageRow[];
  warning?: string;
}

export async function listEntityImagesForArticle(
  articleId: string,
  params?: { limit?: number },
): Promise<EntityImagesResponse> {
  const search = new URLSearchParams();
  if (params?.limit) search.set('limit', String(params.limit));
  const qs = search.toString();
  const response = await apiClient.get(
    `/articles/${articleId}/entity-images${qs ? `?${qs}` : ''}`,
  );
  return response.data as EntityImagesResponse;
}

export async function removeArticleImages(
  articleId: string,
  imageIds: string[],
  expectedUpdatedAt: string | null,
  queueRepublish: boolean = true,
): Promise<{
  removed: boolean;
  article_id: string;
  updated_at: string;
  history_id: string;
  removed_image_id: string | null;
  removed_image_ids: string[];
  before_count: number;
  after_count: number;
  publish_jobs: Array<{ job_id: string; target: string; destination: string }>;
}> {
  // Staged batch — the whole selection lands as ONE backend edit (one
  // history row, one re-render, one republish per target), so multiple
  // removals can never fan out into racing publish/auto-promote jobs.
  const response = await apiClient.post(`/articles/${articleId}/images/remove`, {
    image_ids: imageIds,
    expected_updated_at: expectedUpdatedAt,
    queue_republish: queueRepublish,
  });
  return response.data as any;
}

export async function applyArticleImageEdits(
  articleId: string,
  edits: {
    removals: string[];
    captions: Array<{ image_id: string; caption: string | null }>;
    /** Swaps. Role and array position are preserved so figures don't renumber. */
    replacements?: Array<{ image_id: string; new_image_id: string }>;
    /** Restore a stored revision — how a staged undo/redo commits. Exclusive
     *  with the edit lists: the server reads the snapshot itself. */
    from_history_id?: string | null;
    /** Land on preview and go no further. A completed publish job triggers
     *  auto-promote, which fires for ANY successful publish including an
     *  edit's republish — so by default a commit reaches readers. */
    preview_only?: boolean;
  },
  expectedUpdatedAt: string | null,
  queueRepublish: boolean = true,
): Promise<{
  applied: boolean;
  article_id: string;
  updated_at: string;
  history_id: string;
  removed_image_ids: string[];
  caption_edits: Array<{ image_id: string; before: string | null; after: string | null }>;
  before_count: number;
  after_count: number;
  publish_jobs: Array<{ job_id: string; target: string; destination: string }>;
}> {
  // Staged Apply: removals + caption edits land as ONE backend edit —
  // one history row (single undo), one body re-render, one republish
  // per target.
  const response = await apiClient.post(`/articles/${articleId}/images/apply`, {
    removals: edits.removals,
    captions: edits.captions,
    replacements: edits.replacements ?? [],
    from_history_id: edits.from_history_id ?? null,
    preview_only: edits.preview_only ?? false,
    expected_updated_at: expectedUpdatedAt,
    queue_republish: queueRepublish,
  });
  return response.data as any;
}

export async function suppressArticleImageFallback(
  articleId: string,
  expectedUpdatedAt: string | null,
): Promise<{
  applied: boolean;
  article_id: string;
  updated_at: string;
  history_id: string;
  fallback_suppressed: boolean;
  publish_jobs: Array<{ job_id: string; target: string; destination: string }>;
}> {
  // Preparer-empty articles can still render images on the live site via
  // the publishing service's legacy fallback (raw announcement pool).
  // Suppression records an operator-curated empty set so the renderer's
  // imageless gate fires — per article, so masthead siblings keep their
  // fallback render unless suppressed individually.
  const response = await apiClient.post(`/articles/${articleId}/images/apply`, {
    removals: [],
    captions: [],
    suppress_fallback: true,
    expected_updated_at: expectedUpdatedAt,
  });
  return response.data as any;
}

export async function updateArticleTitle(
  articleId: string,
  articleTitle: string,
  expectedUpdatedAt: string | null,
  editReason?: string,
): Promise<{
  updated: boolean;
  article_id: string;
  article_title: string;
  updated_at: string;
}> {
  // Title edits never move the URL — published_slug is frozen at first
  // publish. Save-only, like body edits: use Push edits → test, then
  // Promote, to ship the corrected title.
  const response = await apiClient.put(`/articles/${articleId}/title`, {
    article_title: articleTitle,
    expected_updated_at: expectedUpdatedAt,
    edit_reason: editReason,
  });
  return response.data as any;
}

export async function replaceArticleImage(
  articleId: string,
  imageId: string,
  newImageId: string,
  expectedUpdatedAt: string | null,
  queueRepublish: boolean = true,
): Promise<{
  replaced: boolean;
  article_id: string;
  updated_at: string;
  history_id: string;
  swapped_image_id: string;
  new_image_id: string;
  preserved_role: string;
  publish_jobs: Array<{ job_id: string; target: string; destination: string }>;
}> {
  const response = await apiClient.post(`/articles/${articleId}/images/replace`, {
    image_id: imageId,
    new_image_id: newImageId,
    expected_updated_at: expectedUpdatedAt,
    queue_republish: queueRepublish,
  });
  return response.data as any;
}

/**
 * Inline body edit. `expectedUpdatedAt` is the optimistic-lock token —
 * the `updated_at` value from the GET that seeded the editor. Backend
 * returns 409 with `current: {updated_at, edited_by, edited_at}` if
 * another operator changed the article in between; handleMutationConflict
 * surfaces the toast and invalidates the article query.
 */
export async function updateArticleContent(
  articleId: string,
  body: { article_content: string; prepared_content?: string | null },
  expectedUpdatedAt: string | null,
): Promise<{ updated: boolean; article_id: string; updated_at: string }> {
  const response = await apiClient.put<{
    updated: boolean;
    article_id: string;
    updated_at: string;
  }>(`/articles/${articleId}/content`, {
    ...body,
    expected_updated_at: expectedUpdatedAt,
  });
  return response.data;
}

/**
 * Correct the company/ticker of an article's source announcement by
 * re-binding it to the chosen entity. Updates the announcement
 * (company_code/company_name/entity_id; exchange_code preserved) and all
 * sibling articles, heals Cynapse, and re-publishes published articles so
 * the live badge self-corrects. `expectedUpdatedAt` is the optimistic-lock
 * token (article.updated_at) — a 409 is thrown if another operator saved.
 */
export async function correctArticleCompany(
  articleId: string,
  body: { target_entity_id: string },
  expectedUpdatedAt: string | null,
): Promise<{
  corrected: boolean;
  announcement_id: string;
  from_company_code: string | null;
  to_company_code: string;
  articles_republished: number;
  publish_jobs: Array<{ article_id: string; target: string; destination: string }>;
  cynapse: { status: string; rebound?: boolean; old_entity_id?: string | null; contacts_affected?: number; relationships_moved?: number; error?: string };
}> {
  const response = await apiClient.post(`/articles/${articleId}/correct-company`, {
    ...body,
    expected_updated_at: expectedUpdatedAt,
  });
  return response.data;
}

/**
 * Archive an article (soft delete)
 * Sets publish_status to 'archived'. Cannot archive published articles.
 */
export async function archiveArticle(articleId: string): Promise<SuccessResponse> {
  const response = await apiClient.post<SuccessResponse>(`/articles/${articleId}/archive`);
  return response.data;
}

/**
 * Snapshot-promote an article to production. The publishing service
 * snapshots the article's source file(s) from `test` onto a fresh branch
 * off `main` and merges. Other commits on test are unaffected.
 *
 * When `targetName` is omitted, the article is promoted to every target
 * where it has a successful test_branch publish.
 */
export async function promoteArticle(
  articleId: string,
  targetName?: string
): Promise<{ success: boolean; pr_url?: string; pr_number?: number; message: string }> {
  const response = await apiClient.post(`/articles/${articleId}/promote`,
    targetName ? { target_name: targetName } : {}
  );
  // Backend swallows job-create failures (e.g. CHECK-constraint rejection)
  // and returns 202 with `{success: false, message: "..."}`. Without this
  // throw, the bulk-promote mutation's Promise.allSettled treats every
  // 2xx as a successful enqueue and tells the operator "N PRs created"
  // when nothing actually queued. Rejecting here surfaces the failure
  // through the existing failures path in the UI.
  if (response.data && response.data.success === false) {
    throw new Error(response.data.message || 'Promote job creation failed');
  }
  return response.data;
}

/**
 * Move a Market Floor article between sections (companies / deals / markets /
 * economy / policy / people / world / opinion).
 *
 * Backend updates article_metadata.section, queues a test republish, and
 * queues a promote_article job. The republish flow handles file relocation
 * on the test branch (removes old section file, writes new) and records
 * the relocation in publishing_history so promote_article only sees the
 * new section path.
 */
export const MARKETFLOOR_SECTIONS = [
  'companies', 'deals', 'markets', 'economy',
  'policy', 'people', 'world', 'opinion',
] as const;
export type MarketFloorSection = typeof MARKETFLOOR_SECTIONS[number];

export async function setMarketFloorSection(
  articleId: string,
  section: MarketFloorSection,
  expectedUpdatedAt: string | null = null,
): Promise<{ job_id: string; status: string; message: string }> {
  const response = await apiClient.post(
    `/articles/${articleId}/marketfloor-section`,
    { section, expected_updated_at: expectedUpdatedAt }
  );
  return response.data;
}

/**
 * Get recent publish/unpublish pipeline jobs for dashboard visibility
 */
export async function getPublishingQueue(limit = 20): Promise<any[]> {
  const response = await apiClient.get<any[]>('/articles/publishing-queue', { params: { limit } });
  return response.data;
}

/**
 * Editorial voice with associated personas, returned by the voices endpoint.
 */
export interface VoicePersona {
  slug: string;
  display_name: string;
  initials: string;
  analytical_lens: string;
}

export interface EditorialVoice {
  voice_id: string;
  voice_name: string;
  voice_type: string;
  description: string;
  personas: VoicePersona[];
}

/**
 * List active editorial voices with their personas.
 * Used by the Generate New Version dialog.
 */
export async function listVoices(): Promise<{ voices: EditorialVoice[] }> {
  const response = await apiClient.get<{ voices: EditorialVoice[] }>('/articles/voices');
  return response.data;
}

// Article Relationships

export interface ArticleRelationship {
  relationship_id: string;
  source_article_id: string;
  target_article_id: string;
  relationship_type: string;
  created_at: string;
  created_by: string;
  related_title: string;
  related_article_id: string;
  direction: 'outgoing' | 'incoming';
  related_target_name: string | null;
}

export async function getArticleRelationships(articleId: string): Promise<ArticleRelationship[]> {
  const response = await apiClient.get<ArticleRelationship[]>(`/articles/${articleId}/relationships`);
  return response.data;
}

export async function createArticleRelationship(
  articleId: string,
  targetArticleId: string,
  relationshipType: string,
): Promise<Record<string, any>> {
  const response = await apiClient.post(`/articles/${articleId}/relationships`, {
    target_article_id: targetArticleId,
    relationship_type: relationshipType,
  });
  return response.data;
}

export async function deleteArticleRelationship(relationshipId: string): Promise<Record<string, any>> {
  const response = await apiClient.delete(`/articles/relationships/${relationshipId}`);
  return response.data;
}

// ============================================================================
// Rewrite-review (mig 114, Step 15 cap)
// ============================================================================

/** One row from article_rewrite_decisions. */
export interface RewriteDecision {
  decision_id: string;
  article_id: string;
  plan_item_id: string | null;
  triggering_announcement_id: string | null;
  decision_state: 'pending' | 'approved' | 'rejected' | 'auto';
  proposed_source_bundle: {
    primary_announcement_id?: string | null;
    source_announcement_ids?: string[];
    action?: string;
    product_id?: string;
    rationale?: string;
    triggering_announcement_id?: string;
  };
  triage_rationale: string | null;
  operator_reason: string | null;
  operator_email: string | null;
  rewrite_count_before: number;
  created_at: string;
  decided_at: string | null;
}

export interface RewriteDecisionsResponse {
  article_id: string;
  rewrite_count: number;
  operator_edited: boolean;
  decisions: RewriteDecision[];
}

export async function getRewriteDecisions(
  articleId: string,
): Promise<RewriteDecisionsResponse> {
  const response = await apiClient.get<RewriteDecisionsResponse>(
    `/articles/${articleId}/rewrite-decisions`,
  );
  return response.data;
}

export async function approveRewriteDecision(
  articleId: string,
  decisionId: string,
  operatorReason?: string,
): Promise<{ status: string; article_id: string; decision_id: string }> {
  const response = await apiClient.post(
    `/articles/${articleId}/rewrite-decisions/${decisionId}/approve`,
    operatorReason ? { operator_reason: operatorReason } : {},
  );
  return response.data;
}

export async function rejectRewriteDecision(
  articleId: string,
  decisionId: string,
  operatorReason?: string,
): Promise<{ status: string; article_id: string; decision_id: string }> {
  const response = await apiClient.post(
    `/articles/${articleId}/rewrite-decisions/${decisionId}/reject`,
    operatorReason ? { operator_reason: operatorReason } : {},
  );
  return response.data;
}

// ============================================================================
// Source bundle (Phase 2 visibility)
// ============================================================================

export interface ArticleSource {
  announcement_id: string;
  role: string | null;
  title: string;
  doc_form: string | null;
  editorial_substance: number | null;
  activity_sub_type: string | null;
  editorial_category: string | null;
  announcement_date: string;
  source_name: string | null;
  company_code: string | null;
  // Curator (Phase 4) — present when the LLM curator ran on this bundle.
  curator_role?: 'anchor' | 'supporting' | 'cite_only' | 'exclude' | null;
  curator_rationale?: string | null;
  curator_provider?: string | null;
  operator_override_role?: 'anchor' | 'supporting' | 'cite_only' | 'exclude' | null;
  operator_override_by?: string | null;
  operator_override_at?: string | null;
}

export interface ArticlePlanItem {
  item_id: string;
  primary_announcement_id: string;
  action: string;
  triage_rationale: string | null;
  plan_created_at: string;
  is_secondary: boolean;
  product_name: string | null;
  target_name: string | null;
  persona_slug: string | null;
}

export interface RewriteHistoryEntry {
  decision_id: string;
  decision_state: 'auto' | 'approved' | 'rejected';
  triage_rationale: string | null;
  operator_email: string | null;
  operator_reason: string | null;
  rewrite_count_before: number;
  created_at: string;
  decided_at: string | null;
}

export interface ArticleSourcesResponse {
  article_id: string;
  plan_item: ArticlePlanItem | null;
  sources: ArticleSource[];
  rewrite_history: RewriteHistoryEntry[];
}

export async function getArticleSources(
  articleId: string,
): Promise<ArticleSourcesResponse> {
  const response = await apiClient.get<ArticleSourcesResponse>(
    `/articles/${articleId}/sources`,
  );
  return response.data;
}

export async function overrideSourceRole(
  articleId: string,
  announcementId: string,
  role: 'anchor' | 'supporting' | 'cite_only' | 'exclude',
): Promise<{ article_id: string; announcement_id: string; role: string }> {
  const response = await apiClient.put(
    `/articles/${articleId}/sources/${announcementId}/role`,
    { role },
  );
  return response.data;
}

export interface ReferencedPeriod {
  period_id: string;
  ticker: string;
  subject_type: 'announcements' | 'trading';
  purpose: string | null;
  start_date: string | null;
  end_date: string | null;
  verbatim: string | null;
  narrative_centrality: 'central' | 'peripheral' | null;
  backfill_decision: 'enrich' | 'skip' | null;
  backfill_decision_reason: string | null;
  back_source_status:
    | 'pending'
    | 'in_progress'
    | 'resolved'
    | 'partial'
    | 'not_found'
    | 'out_of_scope'
    | 'timed_out'
    | 'error'
    | null;
  back_source_completed_at: string | null;
  chart_count: number;
  days_traded: number;
  announcements_ingested: number;
  zoom_days: number;
}

export async function getArticleReferencedPeriods(
  articleId: string,
): Promise<ReferencedPeriod[]> {
  const response = await apiClient.get<ReferencedPeriod[]>(
    `/articles/${articleId}/referenced-periods`,
  );
  return response.data;
}

/** Public corrections disclosure (2026-07-28): append one change-history
 *  entry — rendered at the article foot on the masthead + CorrectionComment
 *  JSON-LD. Append-only; original publish date/sort order untouched. */
export async function addArticleCorrection(
  articleId: string,
  body: { note: string; kind?: 'correction' | 'update'; republish?: boolean },
): Promise<{ added: boolean; entry: { at: string; note: string; kind: string }; republish_job_id: string | null }> {
  const { data } = await apiClient.post(`/articles/${articleId}/corrections`, body);
  return data;
}
